/**
 * Die Einladung eines Bewohners — `/einladung?token=` (HM-081, ADR 0026 §3).
 *
 * **Ohne Anmeldung erreichbar**, wie die Adressen aus HM-026: Wer den Link aus
 * der Nachricht öffnet, hat oft noch kein Konto. Die Seite zeigt zuerst, wohin
 * die Einladung führt — Anlage, Wohnung, Einzug —, damit jemand erkennt, ob sie
 * ihn meint, bevor er etwas eingibt.
 *
 * Danach einer von zwei Wegen, den der Server über `hasAccount` vorgibt:
 * **Registrieren** mit demselben zweistufigen Ablauf wie die Anmeldeseite
 * (Angaben, dann der Sicherheitscode), nur ohne Anlage — aus einer Einladung
 * entsteht ein Bewohner, kein Account —, oder **Anmelden und annehmen**: Wer
 * angemeldet ist, sieht einen Knopf; wer es nicht ist, geht zur Anmeldung und
 * kommt hierher zurück, weil die Adresse stehen bleibt.
 *
 * **Seit HM-160 auch die Einladung zu einer geteilten Anlage** (ADR 0043) —
 * derselbe Link, dieselben zwei Wege. Statt Wohnung und Einzug nennt die Seite
 * die Anlage und die Stufe der Teilung.
 *
 * **Die E-Mail-Adresse ist vorgegeben und nicht änderbar.** Eine Einladung gilt
 * für genau eine Adresse; ein weitergeleiteter Link soll nicht auf ein fremdes
 * Konto einlösbar sein.
 */
import { useId, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Input } from "@hausmeter/ui";
import { AuthBackLink, AuthHeading, AuthLayout, AuthNote, AuthSteps } from "../components/auth-layout.js";
import { CodeField } from "../components/code-field.js";
import { PasswordField } from "../components/password-field.js";
import { LEGAL_META } from "./legal.js";
import { TERMS_VERSION } from "../lib/legal.js";
import { useInvitationPreview } from "../lib/queries.js";
import {
  fieldErrorsFromDetails,
  PASSWORD_MIN_LENGTH,
  registrationErrors,
  type FieldErrors,
  type RegistrationField,
} from "../lib/registration-fields.js";
import { navigate } from "../lib/router.js";
import {
  acceptInvitation,
  confirmRegistration,
  RequestError,
  resendRegistrationCode,
  startRegistration,
  type CurrentUser,
} from "../lib/session.js";
import type { ApiComponents } from "@hausmeter/sdk";

type CodeStatus = ApiComponents["schemas"]["RegistrationStarted"];

const MUTED = { fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 } as const;

function formatDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)),
  );
}

export function InvitationPage({ token, user }: { readonly token: string | null; readonly user: CurrentUser | null }) {
  const preview = useInvitationPreview(token);

  if (token === null) {
    return (
      <AuthLayout headline="Ein Link, eine Wohnung." lead="Die Einladung führt zu den Werten einer Wohneinheit.">
        <AuthBackLink to={{ kind: "start" }}>Zur Anmeldung</AuthBackLink>
        <AuthHeading title="Einladung unvollständig" lead="Der Link enthält keinen Einladungscode. Öffne ihn noch einmal aus der E-Mail." />
      </AuthLayout>
    );
  }

  if (preview.isPending) {
    return (
      <AuthLayout headline="Ein Link, eine Wohnung." lead="Die Einladung wird geprüft.">
        <p style={MUTED}>Wird geladen …</p>
      </AuthLayout>
    );
  }

  if (preview.isError || preview.data === undefined) {
    return (
      <AuthLayout headline="Ein Link, eine Wohnung." lead="Die Einladung führt zu den Werten einer Wohneinheit.">
        <AuthBackLink to={{ kind: "start" }}>Zur Anmeldung</AuthBackLink>
        <AuthHeading
          title="Einladung nicht mehr gültig"
          lead="Sie ist unbekannt, abgelaufen oder wurde zurückgenommen. Bitte den Vermieter um eine neue."
        />
      </AuthLayout>
    );
  }

  const invitation = preview.data;
  const isShare = invitation.kind === "site_share";
  const headline = isShare ? "Eine Anlage, mit dir geteilt." : "Deine Wohnung, deine Werte.";
  const lead = isShare
    ? `„${invitation.siteName}" — ${
        invitation.shareAccess === "write" ? "lesen und ändern" : "lesen"
      }. Löschen und Teilen bleiben beim Besitzer.`
    : `„${invitation.unitName ?? ""}" in „${invitation.siteName}" — ab dem ${formatDate(invitation.movedInOn ?? "")}${
        invitation.movedOutOn === null ? "" : ` bis zum ${formatDate(invitation.movedOutOn)}`
      }.`;
  const accountNote = isShare
    ? "Für diese Einladung entsteht ein Konto ohne eigene Anlage — es zeigt die geteilte Anlage."
    : "Für diese Einladung entsteht ein Konto ohne eigene Anlage — es zeigt deine Wohnung.";

  if (user !== null) {
    return <AcceptWithAccount token={token} user={user} email={invitation.email} lead={lead} headline={headline} />;
  }
  if (invitation.hasAccount) {
    return (
      <AuthLayout headline={headline} lead={lead}>
        <AuthHeading
          title="Mit bestehendem Konto annehmen"
          lead={`Zu ${invitation.email} gibt es schon ein Konto. Melde dich an — danach kommst du hierher zurück und nimmst die Einladung an.`}
        />
        <Button block onClick={() => navigate({ kind: "start" })}>
          Zur Anmeldung
        </Button>
      </AuthLayout>
    );
  }
  return (
    <RegisterWithInvitation token={token} email={invitation.email} lead={lead} headline={headline} accountNote={accountNote} />
  );
}

/* -------------------------------------------------------------------------- */

function AcceptWithAccount({
  token,
  user,
  email,
  lead,
  headline,
}: {
  token: string;
  user: CurrentUser;
  email: string;
  lead: string;
  headline: string;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = user.email.toLowerCase() !== email.toLowerCase();

  async function accept(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation(token);
      // Wohnungen und geteilte Anlagen hängen am Nutzer; beide Listen sind jetzt veraltet.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate({ kind: "start" }, { replace: true });
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout headline={headline} lead={lead}>
      <AuthBackLink to={{ kind: "start" }}>Zur Anwendung</AuthBackLink>
      <AuthHeading
        title="Einladung annehmen"
        lead={mismatch ? `Du bist als ${user.email} angemeldet.` : `Als ${user.email} — die Adresse, an die die Einladung ging.`}
      />
      {mismatch ? (
        <Alert tone="warning">
          Diese Einladung gilt für <strong>{email}</strong>. Melde dich mit diesem Konto an, oder
          lass dich an deine Adresse einladen.
        </Alert>
      ) : (
        <Button block disabled={busy} onClick={() => void accept()}>
          {busy ? "Wird angenommen …" : "Einladung annehmen"}
        </Button>
      )}
      {error !== null && <Alert tone="danger">{error}</Alert>}
    </AuthLayout>
  );
}

/* -------------------------------------------------------------------------- */

/** Die Felder in der Reihenfolge, in der sie dastehen — ohne die feste Adresse. */
const INVITATION_FIELDS: readonly RegistrationField[] = ["firstName", "lastName", "password", "terms"];

function RegisterWithInvitation({
  token,
  email,
  lead,
  headline,
  accountNote,
}: {
  token: string;
  email: string;
  lead: string;
  headline: string;
  accountNote: string;
}) {
  const [sent, setSent] = useState<CodeStatus | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Dieselbe Prüfung am Feld wie auf der Anmeldeseite (`lib/registration-fields.ts`):
     Fehler erst nach dem ersten Absenden, dann bei jedem Tastendruck neu. Die
     Adresse steht fest und wird nicht geprüft — sie kommt aus der Einladung. */
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const idPrefix = useId();
  const fieldId = (field: RegistrationField) => `${idPrefix}-${field}`;
  const atCodeStep = sent !== null;
  const { email: _fixed, ...clientErrors } = registrationErrors({ firstName, lastName, email, password, termsAccepted });
  const errors: FieldErrors = attempted ? { ...serverErrors, ...clientErrors } : serverErrors;

  function edited(field: RegistrationField): void {
    if (serverErrors[field] !== undefined) setServerErrors(({ [field]: _gone, ...rest }) => rest);
  }

  function focusFirst(found: FieldErrors): boolean {
    const first = INVITATION_FIELDS.find((field) => found[field] !== undefined);
    if (first === undefined) return false;
    document.getElementById(fieldId(first))?.focus();
    return true;
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (sent === null) {
      setAttempted(true);
      if (focusFirst(clientErrors)) {
        setError(null);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      if (sent === null) {
        setSent(
          await startRegistration({
            email,
            password,
            firstName,
            lastName,
            locale: "de-DE",
            acceptedTermsVersion: TERMS_VERSION,
            invitationToken: token,
          }),
        );
        setCode("");
      } else {
        await confirmRegistration({ email, code, rememberMe: false });
        /* Die Sitzung hat begonnen; die Anwendung zeigt die Wohnung. Der Token
           verlässt die Adresse — er ist verbraucht. */
        navigate({ kind: "start" }, { replace: true });
      }
    } catch (cause: unknown) {
      const onFields =
        sent === null && cause instanceof RequestError
          ? fieldErrorsFromDetails(cause.details, { firstName, lastName, password })
          : {};
      if (focusFirst(onFields)) {
        setServerErrors(onFields);
        return;
      }
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setSent(await resendRegistrationCode(email));
      setCode("");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      headline={atCodeStep ? "Nur du kannst dieses Konto bestätigen." : headline}
      lead={atCodeStep ? "Der Code gilt zehn Minuten. Nach fünf falschen Eingaben beginnt der Vorgang von vorn." : lead}
    >
      <AuthSteps current={atCodeStep ? 2 : 1} total={2} />
      <AuthHeading
        title={atCodeStep ? "Code eingeben" : "Konto anlegen"}
        lead={
          atCodeStep
            ? `Wir haben ihn an ${email} geschickt.`
            : accountNote
        }
      />
      <form style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }} noValidate onSubmit={(event) => void submit(event)}>
        {atCodeStep ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            <CodeField label="Sicherheitscode" length={sent.codeLength} value={code} onChange={setCode} disabled={busy} autoFocus />
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void resend()}>
              Code erneut senden
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            <Input label="E-Mail-Adresse" value={email} disabled hint="Die Adresse, an die die Einladung ging — sie lässt sich hier nicht ändern." />
            <div style={{ display: "flex", gap: "var(--sp-3)", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 160px" }}>
                <Input id={fieldId("firstName")} label="Vorname" value={firstName} autoComplete="given-name" required error={errors.firstName} onChange={(e) => { setFirstName(e.target.value); edited("firstName"); }} />
              </div>
              <div style={{ flex: "1 1 160px" }}>
                <Input id={fieldId("lastName")} label="Nachname" value={lastName} autoComplete="family-name" required error={errors.lastName} onChange={(e) => { setLastName(e.target.value); edited("lastName"); }} />
              </div>
            </div>
            <PasswordField
              id={fieldId("password")}
              label="Passwort"
              value={password}
              autoComplete="new-password"
              hint={password.length > 0 && password.length < PASSWORD_MIN_LENGTH ? `Noch ${PASSWORD_MIN_LENGTH - password.length} Zeichen — mindestens ${PASSWORD_MIN_LENGTH}.` : `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
              error={errors.password}
              onChange={(e) => { setPassword(e.target.value); edited("password"); }}
            />
            <Checkbox
              id={fieldId("terms")}
              checked={termsAccepted}
              onChange={setTermsAccepted}
              error={errors.terms}
              label={<span style={{ fontSize: 12.5 }}>Ich habe die {LEGAL_META.terms.title} und die {LEGAL_META.privacy.title} gelesen und stimme zu.</span>}
            />
          </div>
        )}
        {error !== null && <Alert tone="danger">{error}</Alert>}
        {/* Gesperrt wird nur noch, solange etwas läuft oder der Code unvollständig ist —
            was im Formular fehlt, sagt ein Klick am Feld, nicht ein grauer Knopf. */}
        <Button type="submit" block disabled={busy || (atCodeStep && code.length !== sent.codeLength)}>
          {busy ? "Bitte warten …" : atCodeStep ? "Konto bestätigen" : "Code anfordern"}
        </Button>
      </form>
      {!atCodeStep && (
        <AuthNote>
          Du hast schon ein Konto mit dieser Adresse? Dann melde dich an und öffne den Link danach noch einmal.
        </AuthNote>
      )}
    </AuthLayout>
  );
}
